import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, MenuItem, Drawer } from "@mui/material";
import {
  ListDocumentsRequest,
  UpdateDocumentRequest,
  CopyDocumentRequest,
  CreateDocumentRequest,
} from "protogen/documents_service_pb";
import { Family, FamilyStatus } from "protogen/advisors_service_pb";
import { Document, DocumentType, DocumentStatus } from "protogen/documents_pb";
import GridPage from "components/layout/GridPage";
import useIsMobile from "components/hooks/useIsMobile";
import useIsDesktop from "components/hooks/useIsDesktop";
import {
  useCreateDocument,
  useListDocuments,
  useUpdateDocument,
  useCopyDocument,
} from "services/documents";
import { useListAdvisorFamilies } from "services/advisor";
import SearchBar from "components/common/SearchBar";
import { Filter } from "components/search/types";
import { entityTypeInFilter } from "components/search/utils";
import { useSearch } from "services/search";
import { RetrievalResult } from "protogen/search_service_pb";
import { StyledMenu } from "components/common/DropdownMenu";
import Checkbox from "components/common/Checkbox";
import DocumentsTable from "components/documents/DocumentsTable";
import ConfirmationDialog, {
  useConfirmationDialog,
} from "components/common/ConfirmationDialog";
import Loading from "components/common/Loading";
import FeaturedDocuments from "components/documents/FeaturedDocuments";
import FlexBox from "components/common/FlexBox";
import useTitle from "components/hooks/useTitle";
import Radio from "components/common/Radio";
import CloseButton from "components/common/CloseButton";
import theme from "theme";

interface CreateButtonProps {
  onCreateDocument: () => void;
  onCreateForm: () => void;
  onCreateList: () => void;
}

const CreateButton = ({
  onCreateDocument,
  onCreateForm,
  onCreateList,
}: CreateButtonProps) => {
  const [createMenuAnchorEl, setCreateMenuAnchorEl] =
    useState<HTMLElement | null>(null);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);

  const handleCreateClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCreateMenuAnchorEl(event.currentTarget);
    setCreateMenuOpen(true);
  };

  const handleCreateClose = () => {
    setCreateMenuAnchorEl(null);
    setCreateMenuOpen(false);
  };

  return (
    <Box>
      <Button size="small" onClick={handleCreateClick}>
        Create document
      </Button>
      <StyledMenu
        anchorEl={createMenuAnchorEl}
        open={createMenuOpen}
        onClose={handleCreateClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <MenuItem
          onClick={() => {
            handleCreateClose();
            onCreateDocument();
          }}
        >
          Document
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCreateClose();
            onCreateForm();
          }}
        >
          Form
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCreateClose();
            onCreateList();
          }}
        >
          List
        </MenuItem>
      </StyledMenu>
    </Box>
  );
};

const filterGroupSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "2px 0",
  cursor: "pointer",
};

const FilterDrawer = ({
  families,
  open,
  onClose,
  checkedFamilies,
  setCheckedFamilies,
  sortOrder,
  setSortOrder,
  checkedDocumentTypes,
  setCheckedDocumentTypes,
}: {
  families: Family[];
  open: boolean;
  onClose: () => void;
  checkedFamilies: Set<string>;
  setCheckedFamilies: (families: Set<string>) => void;
  sortOrder: string;
  setSortOrder: (sortOrder: string) => void;
  checkedDocumentTypes: Set<string>;
  setCheckedDocumentTypes: (documentTypes: Set<string>) => void;
}) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="temporary"
      sx={{
        "& .MuiDrawer-paper": {
          width: 300,
        },
      }}
    >
      <FlexBox direction="column" gap="24px" justifyContent="space-between">
        <FlexBox direction="column" gap="24px">
          <FlexBox
            justifyContent="space-between"
            alignItems="center"
            flex={0}
            sx={{
              padding: "12px 16px 12px 20px",
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 1,
              boxShadow: theme.elevation[1],
            }}
          >
            <Typography variant="h3">Filters</Typography>
            <CloseButton onClick={onClose} />
          </FlexBox>

          {/* Filters scrollable container */}
          <FlexBox
            direction="column"
            sx={{
              padding: "0 24px",
              flexShrink: 1,
              minHeight: 0,
            }}
          >
            {/* Sort by filter */}
            <Box>
              <Typography variant="h5" sx={{ marginBottom: "4px" }}>
                Sort by
              </Typography>
              <Box
                onClick={() => {
                  setSortOrder("last-updated");
                }}
                sx={filterGroupSx}
              >
                <Radio disableRipple checked={sortOrder === "last-updated"} />
                <Typography variant="body">Last updated</Typography>
              </Box>
              <Box
                onClick={() => {
                  setSortOrder("title");
                }}
                sx={filterGroupSx}
              >
                <Radio disableRipple checked={sortOrder === "title"} />
                <Typography variant="body">Name</Typography>
              </Box>
            </Box>

            {/* Document type filter */}
            <Box>
              <Typography variant="h5" sx={{ marginBottom: "4px" }}>
                Type
              </Typography>
              {["Document", "List", "Form", "Form Submissions"].map(
                (documentType) => (
                  <Box
                    key={documentType}
                    onClick={() => {
                      const newFilters = new Set(checkedDocumentTypes);
                      if (newFilters.has(documentType)) {
                        newFilters.delete(documentType);
                      } else {
                        newFilters.add(documentType);
                      }
                      setCheckedDocumentTypes(newFilters);
                    }}
                    sx={filterGroupSx}
                  >
                    <Checkbox
                      disableRipple
                      checked={checkedDocumentTypes.has(documentType)}
                    />
                    <Typography variant="body">{documentType}</Typography>
                  </Box>
                ),
              )}
            </Box>

            {/* Family filter */}
            <Box>
              <Typography variant="h5" sx={{ marginBottom: "4px" }}>
                Family
              </Typography>
              {families.map((family) => (
                <Box
                  key={family.ref}
                  onClick={() => {
                    const newFilters = new Set(checkedFamilies);
                    if (newFilters.has(family.ref)) {
                      newFilters.delete(family.ref);
                    } else {
                      newFilters.add(family.ref);
                    }
                    setCheckedFamilies(newFilters);
                  }}
                  sx={filterGroupSx}
                >
                  <Checkbox
                    disableRipple
                    checked={checkedFamilies.has(family.ref)}
                  />
                  <Typography variant="body">{family.name}</Typography>
                </Box>
              ))}
            </Box>
          </FlexBox>
        </FlexBox>

        <Box
          sx={{
            padding: "20px",
            position: "sticky",
            bottom: 0,
            zIndex: 1,
            backgroundColor: "white",
          }}
        >
          <Button variant="contained" fullWidth onClick={onClose}>
            Apply
          </Button>
        </Box>
      </FlexBox>
    </Drawer>
  );
};

const getSearchFilters = (): Filter[] => {
  const filters = ["document"];
  return [entityTypeInFilter(filters)];
};

export default ({ isCommunity = false }: { isCommunity?: boolean }) => {
  useTitle("Documents");
  const navigate = useNavigate();
  const params = useParams();
  const isMobile = useIsMobile();
  const isDesktop = useIsDesktop();
  const [families, setFamilies] = useState<Family[]>([]);
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [filteredAndSortedDocuments, setFilteredAndSortedDocuments] = useState<
    Document[] | null
  >(null);
  const [sortOrder, setSortOrder] = useState<string>("last-updated");
  const [query, setQuery] = useState<string>("");
  const { request: familiesRequest } = useListAdvisorFamilies();
  const { request: createDocumentRequest } = useCreateDocument();
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { request: listDocumentsRequest } = useListDocuments();
  const [loading, setLoading] = useState(true);
  const { request: copyDocumentRequest } = useCopyDocument();
  const { request: updateDocumentRequest } = useUpdateDocument();
  const { request: searchRequest, data: searchData } = useSearch();
  const confirmState = useConfirmationDialog();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checkedFamilies, setCheckedFamilies] = useState<Set<string>>(
    new Set(),
  );
  const [checkedDocumentTypes, setCheckedDocumentTypes] = useState<Set<string>>(
    new Set(["Document", "List", "Form", "Form Submissions"]),
  );

  const createDocument = async () => {
    const result = await createDocumentRequest();
    navigate(`/documents/${encodeURIComponent(result?.document?.uuid || "")}`);
  };

  const createForm = async () => {
    const result = await createDocumentRequest(
      new CreateDocumentRequest({
        documentType: DocumentType.FORM,
      }),
    );
    navigate(
      `/forms/create/${encodeURIComponent(result?.document?.uuid || "")}`,
    );
  };

  const createList = async () => {
    const result = await createDocumentRequest(
      new CreateDocumentRequest({
        documentType: DocumentType.LIST,
      }),
    );
    navigate(`/documents/${encodeURIComponent(result?.document?.uuid || "")}`);
  };

  const _activeFamilies = (families_?: Family[]) =>
    (families_ || []).filter(
      (f) =>
        f.status !== FamilyStatus.DEACTIVATED &&
        f.status !== FamilyStatus.DIGITAL_DEACTIVATED &&
        f.status !== FamilyStatus.DIGITAL_DEACTIVATED_TRIAL &&
        f.status !== FamilyStatus.DEACTIVATED_PROSPECT,
    );

  const onLoad = async () => {
    const familiesData = await familiesRequest();
    const loadedFamilies = familiesData?.families || [];
    const activeFamilies = _activeFamilies(loadedFamilies);
    setFamilies(activeFamilies);
    setCheckedFamilies(new Set(activeFamilies.map((f) => f.ref)));

    listDocuments(activeFamilies.map((f) => f.ref) || []);
  };

  const listDocuments = async (familyRefs: string[], showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    const topics = params.topics
      ? decodeURIComponent(params.topics).split(",")
      : [];
    // Strictly community when we are on the community page with not topics.
    // In other cases we want a mix of community and advisor documents.
    const strictlyCommunityContent = isCommunity && topics.length === 0;
    const r = await listDocumentsRequest(
      new ListDocumentsRequest({
        // On the root community page, we only show documents with topics.
        includeAdvisorDocuments: !strictlyCommunityContent,
        documentTypes: [
          DocumentType.NOTE,
          DocumentType.FORM,
          DocumentType.LIST,
        ],
        familyRefs: strictlyCommunityContent ? [] : familyRefs,
        includeCommunitySharedDocuments: isCommunity,
        anyTopics: topics,
        includeAllFavorites: !isCommunity,
      }),
    );
    let documents = r?.documents || [];
    // Maybe in the future when we have more document types, we can filter at the API level.
    const filteredDocuments = documents.filter(
      (d) => d.creationSource !== "family-quick-note",
    );
    setAllDocuments(filteredDocuments);
    setHasLoadedOnce(true);
    setFilteredAndSortedDocuments(applySortsAndFilters(filteredDocuments));
    if (showLoading) {
      setLoading(false);
    }
  };
  const handleQuery = async (query: string) => {
    if (!query) return;
    searchRequest({
      queryText: query,
      adminSearchMode: false,
      strictlyChronological: false,
      maxResults: 10,
      filters: getSearchFilters().map((filter) => ({
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
      })),
    });
  };

  const onDuplicate = async (document: Document) => {
    const entityType = document?.inboxType === "family" ? "family" : "advisor";
    await copyDocumentRequest(
      new CopyDocumentRequest({
        fromDocumentType: document?.documentType,
        fromEntityType: entityType,
        fromEntityRef: document?.ref,
        toDocumentType: document?.documentType,
        toEntityType: entityType,
        toEntityRef: document?.inboxEntityRef,
        creationSourceName: "note",
        title: document?.title,
        replaceTitle: true,
      }),
    );
    listDocuments(families.map((f) => f.ref) || []);
  };

  const onDelete = async (document: Document) => {
    confirmState.openDialog(async () => {
      await updateDocumentRequest(
        new UpdateDocumentRequest({
          documentRef: document.ref,
          status: DocumentStatus.DELETED,
        }),
      );
      listDocuments(families.map((f) => f.ref) || []);
    });
  };

  const getDocumentTypeCategory = (document: Document): string => {
    if (document.documentType === DocumentType.NOTE) {
      return "Document";
    } else if (document.documentType === DocumentType.LIST) {
      return "List";
    } else if (document.documentType === DocumentType.FORM) {
      let meta: Record<string, any> = {};
      try {
        meta = document.meta ? JSON.parse(document.meta) : {};
      } catch (e) {}
      const isFormDefinition = meta.isFormDefinition;
      return isFormDefinition ? "Form" : "Form Submissions";
    }
    return "Document"; // Default fallback
  };

  const applySortsAndFilters = (filtered: Document[]): Document[] => {
    if (query && searchData?.results) {
      const documentMap = allDocuments.reduce(
        (acc: Record<string, Document>, d: Document) => {
          acc[d.ref] = d;
          return acc;
        },
        {},
      );
      filtered = searchData.results
        .map((item: RetrievalResult) => documentMap[item.entityRef])
        .filter((d) => !!d)
        .map((d) => d as Document);
    }

    // Apply document type filter
    filtered = filtered.filter((document) => {
      const documentTypeCategory = getDocumentTypeCategory(document);
      return checkedDocumentTypes.has(documentTypeCategory);
    });
    if (sortOrder === "last-updated") {
      filtered = filtered.sort((a, b) => {
        return (
          new Date(Number(b.lastUpdatedSec) * 1000).getTime() -
          new Date(Number(a.lastUpdatedSec) * 1000).getTime()
        );
      });
    } else {
      filtered = filtered.sort((a, b) => {
        const titleA = (a.title || "Untitled").toLowerCase();
        const titleB = (b.title || "Untitled").toLowerCase();
        return titleA.localeCompare(titleB);
      });
    }
    return filtered;
  };

  // Separate documents into starred and non-starred
  const starredDocuments = (filteredAndSortedDocuments || []).filter(
    (document) => document.isFavorite,
  );

  const otherDocuments = (filteredAndSortedDocuments || []).filter(
    (document) => !document.isFavorite || isCommunity,
  );

  useEffect(() => {
    if (!hasLoadedOnce) return;
    setFilteredAndSortedDocuments(applySortsAndFilters(allDocuments));
  }, [
    allDocuments,
    searchData,
    query,
    checkedFamilies,
    sortOrder,
    checkedDocumentTypes,
  ]);

  useEffect(() => {
    handleQuery(query);
  }, [query]);

  useEffect(() => {
    if (hasLoadedOnce) {
      listDocuments(Array.from(checkedFamilies));
    }
  }, [checkedFamilies]);

  useEffect(() => {
    setFilteredAndSortedDocuments(null);
    onLoad();
  }, [isCommunity]);
  return (
    <GridPage
      sx={{
        maxWidth: "1000px",
        margin: isMobile ? "" : "auto",
        padding: isMobile ? "24px" : "64px 48px",
        gap: "20px",
      }}
    >
      <FlexBox direction="column" gap={isMobile ? "24px" : "32px"}>
        {/* Featured documents section */}
        <Typography variant="display">
          {isCommunity ? "Community documents" : "Documents"}
        </Typography>
        {!isCommunity && <FeaturedDocuments />}

        {/* Document list */}
        <FlexBox direction="column">
          {!isCommunity && <Typography variant="h3">My documents</Typography>}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "10px",
                alignItems: isMobile ? "start" : "center",
                flexDirection: isMobile ? "column" : "row",
                width: isMobile ? "100%" : "auto",
              }}
            >
              <SearchBar
                captureHotkeys={true}
                onQuery={(q) => setQuery(q)}
                expand={false}
                fullWidth={true}
                sx={{
                  width: isMobile ? "100%" : "auto",
                  maxWidth: isDesktop ? "280px" : "none",
                }}
                debounceTimeoutMs={1200}
              />
              <Box
                sx={{
                  display: "flex",
                  width: isMobile ? "100%" : "auto",
                  justifyContent: "space-between",
                }}
              >
                {!isCommunity && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setDrawerOpen(true);
                    }}
                  >
                    Filters
                  </Button>
                )}

                {isMobile && (
                  <CreateButton
                    onCreateDocument={createDocument}
                    onCreateForm={createForm}
                    onCreateList={createList}
                  />
                )}
              </Box>
            </Box>
            {!isMobile && (
              <CreateButton
                onCreateDocument={createDocument}
                onCreateForm={createForm}
                onCreateList={createList}
              />
            )}
          </Box>
        </FlexBox>

        {loading ? (
          <Loading />
        ) : (
          <>
            {/* Starred documents */}
            {!isCommunity && starredDocuments.length > 0 && (
              <Box>
                {isMobile && <Typography variant="h4">Favorites</Typography>}
                <DocumentsTable
                  title="Favorites"
                  documents={starredDocuments}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onFavoriteUpdate={() =>
                    listDocuments(Array.from(checkedFamilies), false)
                  }
                  showFavoriteCount={isCommunity}
                />
              </Box>
            )}

            <Box>
              {isMobile && !isCommunity && (
                <Typography variant="h4">All</Typography>
              )}
              {/* Other Documents Section */}
              {otherDocuments.length > 0 && (
                <DocumentsTable
                  title={!isCommunity ? "All" : ""}
                  documents={otherDocuments}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onFavoriteUpdate={() =>
                    listDocuments(Array.from(checkedFamilies), false)
                  }
                  showFavoriteCount={isCommunity}
                />
              )}
            </Box>
          </>
        )}
      </FlexBox>

      <FilterDrawer
        families={families}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
        checkedFamilies={checkedFamilies}
        setCheckedFamilies={setCheckedFamilies}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        checkedDocumentTypes={checkedDocumentTypes}
        setCheckedDocumentTypes={setCheckedDocumentTypes}
      />
      <ConfirmationDialog
        content={"Are you sure you want to remove this document?"}
        {...confirmState.dialogProps}
      />
    </GridPage>
  );
};
